import type {
  IScormRequest,
  IScormResponse,
  ICrossDomainConfig,
  ScormMethod,
  ScormRuntimeVersion,
  ISaveSettings,
} from './common'
import { DEFAULT_CONFIG, DRIVER_VERSION } from './common'

// Session state machine
export enum SessionState {
  IDLE = 'idle',
  INITIALIZING = 'initializing',
  INITIALIZED = 'initialized',
  TERMINATING = 'terminating',
  TERMINATED = 'terminated',
  ERROR = 'error',
}

type SemverMismatch = 'none' | 'patch' | 'minor' | 'major'

/**
 * Compare two semver versions and return the level of mismatch.
 * Returns 'none' if versions match, 'patch'/'minor'/'major' based on difference.
 */
const compareSemver = (courseVersion: string, stubVersion: string): SemverMismatch => {
  const parseSemver = (v: string): [number, number, number] => {
    const parts = v.split('.').map(Number)
    return [parts[0] || 0, parts[1] || 0, parts[2] || 0]
  }

  const [courseMajor, courseMinor, coursePatch] = parseSemver(courseVersion)
  const [stubMajor, stubMinor, stubPatch] = parseSemver(stubVersion)

  if (courseMajor !== stubMajor) {
    return 'major'
  }
  if (courseMinor !== stubMinor) {
    return 'minor'
  }
  if (coursePatch !== stubPatch) {
    return 'patch'
  }
  return 'none'
}

type PendingRequest = {
  method: ScormMethod
  params?: unknown
  resolve: (result: string) => void
  timeoutId: number
}

type CrossDomainAPI = {
  initialize: (saveSettings?: ISaveSettings) => Promise<boolean>
  terminate: () => Promise<boolean>
  closeWindow: () => Promise<boolean>
  loadvalue: (element: string) => Promise<string>
  setvalue: (element: string, value: string) => Promise<boolean>
  setBatch: (values: Array<{ element: string; value: string }>) => Promise<boolean>
  flush: () => Promise<boolean> // v1.1.0
  commit: () => Promise<boolean>
  getLastError: () => Promise<string>
  getErrorString: (code: string) => Promise<string>
  getDiagnostic: (code: string) => Promise<string>
  getVersion: () => ScormRuntimeVersion
  isConnected: () => boolean
  getSessionState: () => SessionState
  postCourseReady: () => Promise<void>
}

// Get channelId from URL query parameter
const getChannelIdFromUrl = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined
  }
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('scorm_channel') || undefined
}

export class CrossDomainClient {
  private config: ICrossDomainConfig
  private messageId = 0
  private pendingRequests: Map<string, PendingRequest> = new Map()
  private sessionState: SessionState = SessionState.IDLE
  private currentVersion: ScormRuntimeVersion = 'none'
  private driverVersion: string | null = null
  private readonly maxRetries = 3
  private readonly retryDelay = 1000
  private channelId: string | undefined

  constructor(config: Partial<ICrossDomainConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config }
    this.channelId = getChannelIdFromUrl()
    this.setupListener()

    if (this.config.debug && this.channelId) {
      console.info('[CrossDomainClient] Using channelId:', this.channelId)
    }
  }

  getChannelId(): string | undefined {
    return this.channelId
  }

  private setupListener(): void {
    window.addEventListener('message', (event) => {
      // Security: only accept from parent
      if (event.source !== window.parent) {
        return
      }

      const data = event.data as IScormResponse
      if (!data || data.type !== 'scorm_response') {
        return
      }

      // Filter by channelId - only process responses for this client's channel
      // If we have a channelId, only accept responses with matching channelId
      if (this.channelId && data.channelId && data.channelId !== this.channelId) {
        if (this.config.debug) {
          console.info(
            '[CrossDomainClient] Ignoring response for different channel:',
            data.channelId,
          )
        }
        return
      }

      const pending = this.pendingRequests.get(data.id)
      if (!pending) {
        return
      }

      this.pendingRequests.delete(data.id)
      window.clearTimeout(pending.timeoutId)

      if (this.config.debug) {
        console.info('[CrossDomainClient] Received response:', data)
        if (data.error) {
          console.error('[CrossDomainClient] LMS error:', data.error)
        }
      }

      this.applyResponseSideEffects(pending.method, pending.params, data.result, data.error)
      pending.resolve(data.result)
    })
  }

  private async callAPIAsync(
    method: ScormMethod,
    params?: unknown,
    timeout?: number,
  ): Promise<string> {
    const messageId = `msg_${this.messageId++}`
    const requestTimeout = timeout ?? this.getTimeout(method)

    return new Promise((resolve) => {
      const timeoutId = window.setTimeout(() => {
        this.pendingRequests.delete(messageId)
        if (this.config.debug) {
          console.warn('[CrossDomainClient] Call timed out:', method, params)
        }
        resolve(this.getFallbackResult(method))
      }, requestTimeout)

      this.pendingRequests.set(messageId, {
        method,
        params,
        resolve,
        timeoutId,
      })

      this.sendMessage(method, params, messageId)
    })
  }

  private getTimeout(method: ScormMethod): number {
    const baseTimeout = this.config.timeout ?? DEFAULT_CONFIG.timeout ?? 5000
    switch (method) {
      case 'initialize':
      case 'terminate':
      case 'commit':
        return Math.max(baseTimeout, 1000)
      default:
        return baseTimeout
    }
  }

  private getFallbackResult(method: ScormMethod): string {
    switch (method) {
      case 'getLastError':
        return '0'
      case 'getErrorString':
      case 'getDiagnostic':
        return ''
      case 'getVersion':
        return 'none'
      case 'getDriverVersion':
        return 'unknown'
      default:
        return 'false'
    }
  }

  private applyResponseSideEffects(
    method: ScormMethod,
    _params: unknown,
    result: string,
    _error?: string,
  ): void {
    if (method === 'getVersion') {
      if (result === '1.2' || result === '2004') {
        this.currentVersion = result
      } else {
        this.currentVersion = 'none'
      }
    }

    if (method === 'getDriverVersion') {
      this.driverVersion = result
    }
  }

  private sendMessage(method: ScormMethod, params?: unknown, id?: string): void {
    const messageId = id || `msg_${this.messageId++}`

    const request: IScormRequest = {
      type: 'scorm_call',
      id: messageId,
      method: method,
      params: params,
      channelId: this.channelId,
    }

    if (this.config.debug) {
      console.info('[CrossDomainClient] Sending:', request)
    }

    window.parent.postMessage(request, this.config.targetOrigin || '*')
  }

  async initialize(saveSettings?: ISaveSettings): Promise<boolean> {
    if (this.sessionState === SessionState.INITIALIZED) {
      console.warn('[CrossDomainClient] Already initialized')
      return true
    }

    if (this.sessionState === SessionState.TERMINATED) {
      console.error('[CrossDomainClient] Cannot initialize after terminate')
      return false
    }

    if (this.sessionState === SessionState.INITIALIZING) {
      console.warn('[CrossDomainClient] Already initializing')
      return false
    }

    // Allow re-initialization after error state
    if (this.sessionState === SessionState.ERROR) {
      console.info('[CrossDomainClient] Retrying initialization after previous error')
      this.sessionState = SessionState.IDLE
    }

    this.sessionState = SessionState.INITIALIZING

    try {
      const stubVersion = await this.callAPIAsync('getDriverVersion')
      this.driverVersion = stubVersion

      if (stubVersion !== 'unknown') {
        const mismatch = compareSemver(DRIVER_VERSION, stubVersion)
        const message = `SCORM driver version mismatch: Course=${DRIVER_VERSION}, Stub=${stubVersion}`

        if (mismatch === 'major') {
          alert(
            `WARNING: ${message}\n\nFor proper operation, the SCORM package needs to be reloaded in the LMS.\nPlease contact your administrator.`,
          )
          console.error('[CrossDomainClient]', message)
          this.sessionState = SessionState.ERROR
          return false
        } else if (mismatch === 'minor') {
          console.error('[CrossDomainClient]', message, '- minor version difference, proceeding')
        } else if (mismatch === 'patch') {
          console.warn('[CrossDomainClient]', message, '- patch version difference')
        }
      }

      for (let attempt = 0; attempt < this.maxRetries; attempt++) {
        const result = await this.callAPIAsync('initialize', saveSettings)

        if (result === 'true') {
          this.sessionState = SessionState.INITIALIZED

          // Get SCORM version
          const version = await this.callAPIAsync('getVersion')
          if (version === '1.2' || version === '2004') {
            this.currentVersion = version
          }

          console.info(
            `[CrossDomainClient v${DRIVER_VERSION}] Initialized, SCORM version:`,
            this.currentVersion,
          )

          return true
        }

        if (attempt < this.maxRetries - 1) {
          console.warn(`[CrossDomainClient] Initialize attempt ${attempt + 1} failed, retrying...`)
          await this.delay(this.retryDelay * Math.pow(2, attempt))
        }
      }

      console.error('[CrossDomainClient] Failed to initialize after', this.maxRetries, 'attempts')
      this.sessionState = SessionState.ERROR
      return false
    } catch (error) {
      console.error('[CrossDomainClient] Initialize error:', error)
      this.sessionState = SessionState.ERROR
      return false
    }
  }

  async terminate(): Promise<boolean> {
    if (this.sessionState === SessionState.TERMINATING) {
      console.warn('[CrossDomainClient] Already terminating')
      return false
    }

    if (this.sessionState !== SessionState.INITIALIZED) {
      console.warn(
        '[CrossDomainClient] Cannot terminate: not initialized, state:',
        this.sessionState,
      )
      return false
    }

    this.sessionState = SessionState.TERMINATING

    try {
      const result = await this.callAPIAsync('terminate')

      if (result === 'true') {
        this.sessionState = SessionState.TERMINATED
        console.info('[CrossDomainClient] Terminated successfully')

        return true
      }

      console.error('[CrossDomainClient] Terminate failed')
      this.sessionState = SessionState.ERROR
      return false
    } catch (error) {
      console.error('[CrossDomainClient] Terminate error:', error)
      this.sessionState = SessionState.ERROR
      return false
    }
  }

  async closeWindow(): Promise<boolean> {
    console.info('[CrossDomainClient] Requesting window close')

    await this.delay(100)

    const result = await this.callAPIAsync('closeWindow')
    return result === 'true'
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  async loadvalue(element: string): Promise<string> {
    return this.callAPIAsync('getvalue', element)
  }

  async setvalue(element: string, value: string): Promise<boolean> {
    const result = await this.callAPIAsync('setvalue', { element, value })
    return result === 'true'
  }

  async setBatch(values: Array<{ element: string; value: string }>): Promise<boolean> {
    const result = await this.callAPIAsync('setBatch', values)
    return result === 'true'
  }

  async flush(): Promise<boolean> {
    const result = await this.callAPIAsync('flush')
    return result === 'true'
  }

  async commit(): Promise<boolean> {
    const result = await this.callAPIAsync('commit')
    return result === 'true'
  }

  async getLastError(): Promise<string> {
    return this.callAPIAsync('getLastError')
  }

  async getErrorString(code: string): Promise<string> {
    return this.callAPIAsync('getErrorString', code)
  }

  async getDiagnostic(code: string): Promise<string> {
    return this.callAPIAsync('getDiagnostic', code)
  }

  getVersion(): ScormRuntimeVersion {
    return this.currentVersion
  }

  isConnected(): boolean {
    return this.sessionState === SessionState.INITIALIZED
  }

  getSessionState(): SessionState {
    return this.sessionState
  }

  // Phase B.2: fire-and-forget signal that the course UI has mounted.
  // Router side treats the method as successful regardless of response,
  // but we still await so that the message is at least queued.
  async postCourseReady(): Promise<void> {
    await this.callAPIAsync('courseReady')
  }

  createAPI(): CrossDomainAPI {
    return {
      initialize: (saveSettings?: ISaveSettings) => this.initialize(saveSettings),
      terminate: () => this.terminate(),
      closeWindow: () => this.closeWindow(),
      loadvalue: (element: string) => this.loadvalue(element),
      setvalue: (element: string, value: string) => this.setvalue(element, value),
      setBatch: (values: Array<{ element: string; value: string }>) => this.setBatch(values),
      flush: () => this.flush(),
      commit: () => this.commit(),
      getLastError: () => this.getLastError(),
      getErrorString: (code: string) => this.getErrorString(code),
      getDiagnostic: (code: string) => this.getDiagnostic(code),
      getVersion: () => this.getVersion(),
      isConnected: () => this.isConnected(),
      getSessionState: () => this.getSessionState(),
      postCourseReady: () => this.postCourseReady(),
    }
  }
}
